import React, { useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import CONFIG from "../config";
import BRANDING from "../branding";
import SEO from "../components/common/SEO";

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');

  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [step, setStep] = useState(1); // 1 = Email, 2 = Answer
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const currentAppName = isArabic ? BRANDING.appName.ar : BRANDING.appName.en;
  const appInitials = currentAppName
    .trim()
    .split(/[\s-]+/)
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || 'F-S';

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const res = await axios.post(`${CONFIG.API_URL}/api/auth/forgot-password`, { email });
      setSecurityQuestion(res.data.securityQuestion);
      setStep(2);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.message || t('auth.error_occurred', 'An error occurred')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const res = await axios.post(`${CONFIG.API_URL}/api/auth/verify-security-answer`, { email, answer: securityAnswer });
      navigate(`/reset-password?token=${res.data.token}`);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.message || t('auth.error_occurred', 'An error occurred')}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#fcfcfc]">
      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
        <title>
          {isArabic
            ? `نسيت كلمة المرور | ${BRANDING.appName.ar}`
            : `Forgot Password | ${BRANDING.appName.en}`}
        </title>
      </Helmet>
      <SEO />

      <div
        className="w-full max-w-5xl overflow-hidden flex flex-col md:flex-row shadow-2xl"
        style={{ backgroundColor: 'var(--bg-header)', borderRadius: 'var(--card-radius)', border: '1px solid var(--border)' }}
      >
        {/* Left Side - Visual/Branding */}
        <div className="md:w-1/2 bg-gray-900 relative p-12 flex flex-col justify-between text-white overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-primary/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>

          <div className="relative z-10">
            <Link to="/" className="text-2xl font-black tracking-tighter hover:text-brand-primary transition-colors flex items-center gap-2">
              <span className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center text-white text-lg">{appInitials}</span>
              {isArabic ? BRANDING.appName.ar : BRANDING.appName.en}
            </Link>
          </div>

          <div className="relative z-10 mb-12">
            <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
              {t('auth.recover_password', 'Recover Password')}
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-sm">
              {isArabic
                ? "أدخل بريدك الإلكتروني وسنرسل لك رابطاً لإعادة تعيين كلمة المرور الخاصة بك."
                : "Enter your email address and we'll send you a link to reset your password."
              }
            </p>
          </div>

          <div className="relative z-10 flex items-center gap-4 text-sm text-gray-400">
            <span>
              {isArabic ? "أمانك هو أولويتنا" : "Your security is our priority"}
            </span>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="md:w-1/2 p-8 md:p-16 flex flex-col justify-center">
          <div className="max-w-md mx-auto w-full">
            <div className="mb-10 text-center md:text-left">
              <h2 className="text-3xl font-black mb-2" style={{ color: 'var(--text-header)' }}>
                {t('auth.forgot_password', 'Forgot Password?')}
              </h2>
              <p className="text-gray-400 font-medium">
                {step === 1 ? t('auth.forgot_password_desc', "No worries, we'll help you get back in.") : t('auth.answer_security_question', "Answer your security question to continue.")}
              </p>
            </div>

            {step === 1 && (
              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                    {t('auth.email', 'Email Address')}
                  </label>
                  <div className="relative">
                    <i className={`fas fa-envelope absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                    <input
                      type="email"
                      name="email"
                      placeholder={t('auth.email', 'Email')}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                      style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                      value={email}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gray-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-brand-primary hover:shadow-xl hover:shadow-brand-primary/30 active:scale-[0.98] transition-all disabled:bg-gray-400 disabled:cursor-not-allowed transform"
                >
                  {loading ? t('auth.sending', 'Verifying...') : t('auth.continue', 'Continue')}
                </button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleAnswerSubmit} className="space-y-6">
                <div className="p-6 rounded-2xl bg-gray-50 border border-gray-100 text-center shadow-sm">
                  <i className="fas fa-question-circle text-brand-primary text-3xl mb-3"></i>
                  <h3 className="font-bold text-gray-800 text-lg">{securityQuestion}</h3>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                    {t('auth.security_answer', 'Your Answer')}
                  </label>
                  <div className="relative">
                    <i className={`fas fa-key absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                    <input
                      type="text"
                      name="securityAnswer"
                      placeholder={t('auth.answer_placeholder', 'Enter your secret answer')}
                      onChange={(e) => setSecurityAnswer(e.target.value)}
                      className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                      style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                      value={securityAnswer}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-primary text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed transform shadow-xl shadow-brand-primary/30"
                >
                  {loading ? t('auth.verifying', 'Verifying...') : t('auth.verify_answer', 'Verify Answer')}
                </button>
              </form>
            )}

            {message && (
              <div className={`mt-6 p-4 rounded-2xl text-center text-sm font-bold bg-red-50 text-red-700 border border-red-100`}>
                {message}
              </div>
            )}

            <div className="mt-10 text-center space-y-4">
              <p className="text-gray-500 font-medium">
                {t('auth.remembered_password', 'Remembered your password?')} {" "}
                <Link to="/login" className="font-black hover:text-brand-primary transition-colors border-b-2 hover:border-brand-primary" style={{ color: 'var(--text-header)', borderColor: 'var(--text-header)' }}>
                  {t('auth.login', 'Login')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
